import { motion } from "framer-motion";
import { ArrowRight, Terminal } from "lucide-react";
import FireParticles from "./FireParticles";
import BreakingChains from "./BreakingChains";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/174e7555-4c1b-4694-85af-3ff24499f4d9/images/f02a2aec38f25e3e9ca70bca37d9ed7bda614c4e29ef5a089a3b8aa987eaf32a.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center pt-28 pb-20 sm:pt-32 sm:pb-24"
    >
      {/* bg image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-35"
        style={{ backgroundImage: `url(${BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black" />
      <div className="absolute inset-0 tech-grid grid-drift opacity-50" />
      <FireParticles density={110} />

      {/* breaking chains — pushed to the right, lower opacity, sits behind content */}
      <div className="absolute right-[-6%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.22] hidden md:block">
        <BreakingChains className="w-[720px] lg:w-[820px] xl:w-[920px] h-auto" />
      </div>
      {/* mobile: small, centered, subtle chains beneath the tagline */}
      <div className="absolute inset-x-0 bottom-16 pointer-events-none opacity-25 md:hidden">
        <BreakingChains className="w-full max-w-sm mx-auto h-[14vh]" />
      </div>

      {/* content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <span className="w-2 h-2 bg-[#FF4D00] pulse-dot" />
            <span className="chapter-label" data-testid="hero-chapter-label">
              A Benchmark, Stolen from the Gods
            </span>
          </div>

          {/* Wordmark — fluid size using clamp; fits on one line from laptop up,
              stacks gracefully on mobile without crowding */}
          <h1
            data-testid="hero-title"
            className="font-serif-display tracking-tighter text-neutral-100 leading-[0.95]"
            style={{ fontSize: "clamp(2.75rem, 10vw, 7.5rem)" }}
          >
            <span className="block sm:inline">PROMETHEUS</span>
            <span
              className="mx-1 sm:mx-3 text-[#FF4D00] glow-fire align-middle"
              aria-hidden="true"
            >
              ·
            </span>
            <span className="italic text-neutral-400">EBM</span>
          </h1>

          {/* Thin accent line + one-line thesis subtitle, replaces the old
              double-stacked paragraphs that felt congested */}
          <div className="mt-6 sm:mt-8 flex items-start gap-4 max-w-3xl">
            <span className="hidden sm:block mt-3 h-px w-10 bg-[#FF4D00]/70 shrink-0" />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35, duration: 1 }}
              data-testid="hero-tagline"
              className="font-serif-display italic text-neutral-300 leading-[1.25] text-xl sm:text-2xl lg:text-[1.75rem]"
            >
              The fire of true self-knowledge — stolen for those who dream of building AGI.
            </motion.p>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            data-testid="hero-subtitle"
            className="mt-5 sm:mt-6 font-mono text-[11px] sm:text-xs text-neutral-500 max-w-2xl leading-relaxed tracking-wide"
          >
            Woven in solitude by a single Promethean mind. Born not of gold or grand halls, but of an aging machine and absolute devotion.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href="#findings"
              data-testid="hero-cta-latest-results"
              className="btn-terminal group"
            >
              Latest Results
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#install"
              data-testid="hero-cta-install"
              className="btn-terminal btn-terminal-cyan group"
            >
              <Terminal className="w-4 h-4" />
              pip install prometheus-ebm
            </a>
          </motion.div>

          {/* scroll hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-14 sm:mt-16 flex items-center gap-4 text-neutral-600 font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase"
          >
            <span className="w-10 h-px bg-neutral-700" />
            scroll · the fire descends
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
